import React, { useCallback, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import '../../styles/css/summary_modal.css';
import { SummaryBody } from './SummaryBody';
import { SummaryToolbar } from './SummaryToolbar';
import { SummaryButtonGroup } from './SummaryButtonGroup';
import { useAppSelector } from '../../redux/store';
import { useSummary, SummarizePayload } from '../../hooks/useSummary';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import { EditorState } from 'draft-js';
import { convertMarkdownToDocx, downloadDocx } from '../../shared/markdownToDocx';
import { enqueueSnackbar } from 'notistack';

type Props = {
  onClose: () => void;
  data?: string | { text: string; folderName: string };
  editorState?: EditorState;
};

export interface SummaryModalRef {
  handleOnClose: () => void;
}

// Helper function to remove trailing hashes from markdown content
const removeTrailingHashes = (content: string): string => {
  return content.replace(/#+\s*$/gm, '');
};

// Export function for downloading summary as DOCX
const exportSummary = async (summary: string, sessionName: string) => {
  try {
    // Validate input
    if (!summary || summary.trim().length === 0) {
      throw new Error('No content to export');
    }

    const summaryContentToExport: string = removeTrailingHashes(summary);
    const blob = await convertMarkdownToDocx(summaryContentToExport);

    const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const summaryOutputFilename = sessionName + ' (povzetek) - ' + currentDate + '.docx';

    downloadDocx(blob, summaryOutputFilename);
  } catch (error) {
    console.error('Error generating DOCX:', error);
    enqueueSnackbar('Napaka pri izvozu povzetka. Prosimo poizkusite znova.', { variant: 'error' });
  }
};

export const SummaryModal = forwardRef<SummaryModalRef, Props>(({ onClose, data, editorState }, ref) => {
  const [summarizePayload, setSummarizePayload] = useState<SummarizePayload>();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { sessionId, sessionLockKey } = useAppSelector((state) => state.app.editorLock);
  const sessionName = useAppSelector((state) => state.app.sessionName);

  const {
    isLoadingPrompts,
    isLoadingSummary,
    isPending,
    summaryInProgress,
    availablePrompts,
    summary,
    summaryExists,
    availableModels,
    savedSummaryConfig,
    startSummarize,
    initSummarize,
  } = useSummary({
    sessionId: sessionId?.toString() || null,
    textContent: typeof data === 'string' ? data : data?.text,
    editorState: editorState,
    sessionLockKey: sessionLockKey || null,
  });

  useEffect(() => {
    initSummarize();
  }, [initSummarize]);

  const summarizeEnabled = !summaryInProgress && !!summarizePayload;

  const handleOnSummarize = useCallback(async () => {
    if (!summarizePayload) return;
    await startSummarize(summarizePayload);
  }, [summarizePayload, startSummarize]);

  const handleOnExport = useCallback(async () => {
    if (!summary) return;

    // Use folder name from modal data if available (for merged sessions)
    // Otherwise use session name or fallback to 'merged-sessions'
    let sessionNameForExport = 'merged-sessions';

    if (data && typeof data === 'object' && 'folderName' in data) {
      // This is a merged session with folder name from modal data
      sessionNameForExport = data.folderName;
    } else if (sessionName) {
      // This is a regular session, use the session name
      sessionNameForExport = sessionName;
    }

    await exportSummary(summary, sessionNameForExport);
  }, [summary, sessionName, data]);

  const handleOnClose = useCallback(() => {
    if (summaryInProgress) {
      setShowConfirmDialog(true);
    } else {
      onClose();
      setSummarizePayload(undefined);
    }
  }, [onClose, summaryInProgress]);

  useImperativeHandle(
    ref,
    () => ({
      handleOnClose,
    }),
    [handleOnClose]
  );

  const handleConfirmClose = useCallback(() => {
    setShowConfirmDialog(false);
    onClose();
    setSummarizePayload(undefined);
  }, [onClose]);

  const handleCancelClose = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  return (
    <>
      <div className="summary_modal_content_wrapper">
        <div className="summary_modal_header">
          <div className="summary_modal_header_title">Povzetek</div>
        </div>
        <SummaryToolbar
          isLoading={isLoadingPrompts}
          summarizePayload={summarizePayload}
          setSummarizePayload={setSummarizePayload}
          availableStages={availablePrompts.map((prompt) => ({
            id: prompt.id,
            name: prompt.namen,
            isDefault: prompt.is_default,
          }))}
          availableModels={availableModels}
          savedSummaryConfig={savedSummaryConfig}
        />
        <SummaryButtonGroup
          summaryExists={summaryExists}
          isEnabled={summarizeEnabled}
          isPending={isPending}
          summaryInProgress={summaryInProgress}
          onSummarize={handleOnSummarize}
          onExport={handleOnExport}
        />
        <SummaryBody summary={summary} isLoading={isLoadingSummary} />
        <div className="summary_modal_header_close">
          <button onClick={handleOnClose} className="modal_button_disaggre">
            Zapri
          </button>
        </div>
      </div>

      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Potrditev zapiranja</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Ali ste prepričani, da želite zapreti? Povzetek se še vedno ustvarja.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose} color="inherit">
            Nazaj
          </Button>
          <Button onClick={handleConfirmClose} color="primary" variant="contained">
            Zapri
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});
